import { createAction } from '@reduxjs/toolkit';

import type {
	DuelHistoryResponse,
	DuelHubResponse,
	DuelPlayerRewardDto,
	DuelPlayerSeasonDto,
	DuelResultResponse,
	DuelRewardMethod,
	DuelRewardStatus,
	DuelSeasonLeaderboardResponse,
	DuelSeasonProgressResponse,
	DuelSnapshotDto,
} from '../../services/api';
import type { DuelConnectionState, DuelHistoryFilter, DuelRankingScope } from './duelsReducer';

export const duelsScreenMounted = createAction('duels/screen/mounted');
export const duelsScreenUnmounted = createAction('duels/screen/unmounted');
export const duelsConnectionChanged = createAction<DuelConnectionState>('duels/connection/changed');
export const duelsReconnectRequest = createAction('duels/connection/reconnect');

export const duelsSnapshotReceived = createAction<DuelSnapshotDto>('duels/snapshot/received');
export const duelsCurrentReady = createAction('duels/current/ready');
export const duelsCurrentCleared = createAction('duels/current/cleared');
export const duelsCurrentRefresh = createAction('duels/current/refresh');

export const duelsJoinRequest = createAction('duels/join/request');
export const duelsJoinSuccess = createAction<DuelSnapshotDto>('duels/join/success');
export const duelsJoinFailure = createAction<string>('duels/join/failure');
export const duelsJoinErrorDismiss = createAction('duels/join/errorDismiss');
export const duelsSocketError = createAction<{ message: string; code?: string }>(
	'duels/socket/error',
);

export const duelsPlayResume = createAction<string>('duels/play/resume');
export const duelsPlayStop = createAction('duels/play/stop');
export const duelsResultOpened = createAction('duels/play/resultOpened');

export const duelsHubRequest = createAction('duels/hub/request');
export const duelsHubSuccess = createAction<DuelHubResponse>('duels/hub/success');
export const duelsHubFailure = createAction<string>('duels/hub/failure');

export const duelResultFocus = createAction<string>('duels/result/focus');
export const duelResultBlur = createAction<string>('duels/result/blur');
export const duelResultRetry = createAction<string>('duels/result/retry');
export const duelResultSuccess = createAction<DuelResultResponse>('duels/result/success');
export const duelResultFailure = createAction<{ duelId: string; key: string }>(
	'duels/result/failure',
);

export const duelsHistoryRequest = createAction<DuelHistoryFilter>('duels/history/request');
export const duelsHistoryLoadMore = createAction('duels/history/loadMore');
export const duelsHistorySuccess = createAction<{
	filter: DuelHistoryFilter;
	page: DuelHistoryResponse;
	append: boolean;
}>('duels/history/success');
export const duelsHistoryFailure = createAction<string>('duels/history/failure');

export const duelsSeasonsSuccess = createAction<DuelPlayerSeasonDto[]>('duels/seasons/success');

export const duelsSeasonRequest = createAction('duels/season/request');
export const duelsSeasonSuccess = createAction<DuelSeasonProgressResponse>('duels/season/success');
export const duelsSeasonFailure = createAction<string>('duels/season/failure');

export const duelsRewardsRequest = createAction('duels/rewards/request');
export const duelsRewardsSuccess = createAction<DuelPlayerRewardDto[]>('duels/rewards/success');
export const duelsRewardsFailure = createAction<string>('duels/rewards/failure');

export const duelRewardRequestSubmit = createAction<{
	entitlementId: string;
	method: DuelRewardMethod;
	email: string;
}>('duels/reward/requestSubmit');
export const duelRewardCancelSubmit = createAction<string>('duels/reward/cancelSubmit');
export const duelRewardClaimSubmit = createAction<string>('duels/reward/claimSubmit');
export const duelRewardClaimErrorCleared = createAction('duels/reward/claimErrorCleared');
export const duelRewardClaimFailure = createAction<{ id: string; key: string }>(
	'duels/reward/claimFailure',
);
export const duelRewardTransitionSuccess = createAction<{
	id: string;
	from: DuelRewardStatus;
	status: DuelRewardStatus;
	kind: 'request' | 'cancel' | 'claim';
}>('duels/reward/transitionSuccess');
export const duelRewardTransitionFailure = createAction<string>('duels/reward/transitionFailure');
export const duelRewardTransitionReset = createAction('duels/reward/transitionReset');

export const duelsRankingRequest = createAction<DuelRankingScope>('duels/ranking/request');
export const duelsRankingSuccess = createAction<{
	scope: DuelRankingScope;
	board: DuelSeasonLeaderboardResponse | null;
}>('duels/ranking/success');
export const duelsRankingFailure = createAction<{ scope: DuelRankingScope; key: string }>(
	'duels/ranking/failure',
);
